import { useMutation, useQuery } from "@apollo/client";
import { useFormik } from "formik";
import { useEffect, useMemo } from "react";
import * as Yup from "yup";

import { GridInputs } from "../../../../../../../components/Functional";
import { Button, Modal } from "../../../../../../../components/ui";
import { CREATE_INVOICE_SALES_PEOPLE } from "../../../../../../../graphql/mutation/InvoiceSalesPerson";
import { FETCH_INVOICE_SALES_PERSON_TYPES } from "../../../../../../../graphql/query/InvoiceSalesPersonType";
import { useEntities } from "../../../../../../../hooks/useEntities";
import useUser from "../../../../../../../hooks/useUser";
import {
  getEqualSplitRatios,
  getSalesPersonSplitKey,
  getSalesPersonTypeKey,
  getSellingTypeId,
  getSourcingTypeId,
  getSplitAllocationSummary,
  hasSingleSalesPersonTypeGroup,
  isSplitAllocationComplete,
  mergeAdditionalSalesPeople,
  SPLIT_TOTAL_TARGET,
  SPLIT_TARGET_PERCENTAGE,
} from "./salesPersonSplitUtils";

const SECTION_KEYS = {
  selling: "selling",
  sourcing: "sourcing",
};

const SECTIONS = [
  { key: SECTION_KEYS.selling, label: "Selling" },
  { key: SECTION_KEYS.sourcing, label: "Sourcing" },
];

const getSalesPersonUserIdKey = salesPerson =>
  Object.keys(salesPerson || {}).find(key => key.startsWith("salesUserId_"));

const AddSalesPersonBody = props => {
  const { closeModal, invoice, additionalSalesPeople } = props || {};
  const [
    createInvoiceSalesPeople,
    { data: { createInvoiceSalesPeople: { error } = {} } = {}, loading, reset },
  ] = useMutation(CREATE_INVOICE_SALES_PEOPLE, {
    refetchQueries: ["FetchInvoice"],
  });
  const {
    entities: { edges: entities = [] },
  } = useEntities();
  const {
    data: { invoiceSalesPersonTypes = [] } = {},
    loading: salesPersonTypesLoading,
  } = useQuery(FETCH_INVOICE_SALES_PERSON_TYPES);

  const sellingTypeId = useMemo(
    () => getSellingTypeId(invoiceSalesPersonTypes),
    [invoiceSalesPersonTypes],
  );
  const sourcingTypeId = useMemo(
    () => getSourcingTypeId(invoiceSalesPersonTypes),
    [invoiceSalesPersonTypes],
  );

  // Existing rows may have legacy "other" type IDs. Default anything that
  // isn't explicitly sourcing to selling so it shows up in a section.
  const normalizeLoadedTypeId = typeId =>
    typeId === sourcingTypeId ? sourcingTypeId : sellingTypeId;

  const initialSalesPeople = useMemo(
    () =>
      mergeAdditionalSalesPeople(
        invoice?.invoiceSalesPersons || [],
        additionalSalesPeople || [],
      ).map((user, index) => ({
        [`salesUserId_${index + 1}`]: user.salesUserId,
        [`split_${index + 1}`]: user.split,
        [`type_${index + 1}`]: normalizeLoadedTypeId(user.typeId),
        [`action_${index + 1}`]: "removeDynamicInputs",
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [invoice, additionalSalesPeople, sellingTypeId, sourcingTypeId],
  );

  const createDynamicValidation = fieldName => {
    return Yup.object().shape({
      ...(/^split_\d+$/.test(fieldName) && {
        [fieldName]: Yup.number()
          .required("Enter split ratio")
          .min(0, "Invalid Value")
          .max(1, "Invalid Value")
          .typeError("Invalid Value"),
      }),
      ...(/^salesUserId_\d+$/.test(fieldName) && {
        [fieldName]: Yup.number()
          .required("Select a sales user")
          .typeError("Select a sales user"),
      }),
      ...(/^type_\d+$/.test(fieldName) && {
        [fieldName]: Yup.number()
          .required("Select a type")
          .typeError("Select a type"),
      }),
    });
  };

  const dynamicValidation = formData => {
    let validationSchema = Yup.object({
      allocated: Yup.number().test(
        "allocation-validation",
        "Invalid allocation",
        function () {
          const values = this.parent || {};
          const salesPeople = values.salesPeople || [];
          const { allocatedRatio, allocatedPercentage } =
            getSplitAllocationSummary(salesPeople, values);
          const singleTypeGroupOnly = hasSingleSalesPersonTypeGroup(
            salesPeople,
            values,
            invoiceSalesPersonTypes,
          );

          if (salesPeople.length <= 0 && allocatedRatio === 0) {
            return true;
          }

          if (
            singleTypeGroupOnly &&
            allocatedPercentage >= 50 &&
            allocatedPercentage <= SPLIT_TARGET_PERCENTAGE
          ) {
            return true;
          }

          if (isSplitAllocationComplete(allocatedRatio)) {
            return true;
          }

          return this.createError({
            message:
              allocatedPercentage > SPLIT_TARGET_PERCENTAGE ||
              allocatedRatio > SPLIT_TOTAL_TARGET
                ? "Allocated split ratio cannot exceed 100%"
                : singleTypeGroupOnly
                  ? "Allocated split ratio must be between 50% and 100%"
                  : "Allocated split ratio must equal 100%",
          });
        },
      ),
      remaining: Yup.number(),
    });

    Object.keys(formData || {}).forEach(fieldName => {
      validationSchema = validationSchema.concat(
        createDynamicValidation(fieldName),
      );
    });

    return validationSchema;
  };

  const formik = useFormik({
    initialValues: {
      allocated: "0.00000",
      remaining: 100,
      salesPeople: initialSalesPeople,
      ...initialSalesPeople.reduce(
        (merged, row) => Object.assign(merged, row),
        {},
      ),
    },
    enableReinitialize: true,
    validateOnChange: true,
    validateOnMount: true,
    validationSchema: () => dynamicValidation(formik?.values),
    onSubmit: async values => {
      const finalValues = {
        invoiceId: invoice?.id,
        salesUsers: values?.salesPeople?.map(salePerson => {
          const salesUserIdKey = getSalesPersonUserIdKey(salePerson);
          const splitKey = getSalesPersonSplitKey(salePerson);
          const typeKey = getSalesPersonTypeKey(salePerson);

          return {
            salesUserId: values[salesUserIdKey],
            split: values[splitKey],
            typeId: values[typeKey],
          };
        }),
      };

      createInvoiceSalesPeople({
        variables: { input: finalValues },
      }).then(resp => {
        const { data: { createInvoiceSalesPeople: { success } = {} } = {} } =
          resp || {};
        if (success) {
          reset();
          formik?.resetForm?.();
          closeModal?.();
        }
      });
    },
  });

  useEffect(() => {
    const { allocatedRatio, remainingPercentage } = getSplitAllocationSummary(
      formik.values.salesPeople,
      formik.values,
    );
    const allocatedDisplayValue = allocatedRatio.toFixed(5);

    if (formik.values.allocated !== allocatedDisplayValue) {
      formik?.setFieldValue("allocated", allocatedDisplayValue, false);
    }

    if (formik.values.remaining !== remainingPercentage) {
      formik?.setFieldValue("remaining", remainingPercentage, false);
    }
  }, [formik, formik.values, formik.values.salesPeople.length]);

  // Partition all rows into Selling vs Sourcing based on each row's current
  // type id. The absolute index is tracked so row deletion can target the
  // correct slot in formik.values.salesPeople. Anything that doesn't
  // explicitly match the sourcing type falls into selling (handles the brief
  // window before the types query resolves).
  const sectionRowsByKey = useMemo(() => {
    return formik.values.salesPeople.reduce(
      (groups, row, absoluteIndex) => {
        const typeKey = getSalesPersonTypeKey(row);
        const rowTypeId = typeKey ? formik.values[typeKey] : null;
        const sectionKey =
          sourcingTypeId != null && rowTypeId === sourcingTypeId
            ? SECTION_KEYS.sourcing
            : SECTION_KEYS.selling;
        return {
          ...groups,
          [sectionKey]: [...groups[sectionKey], { row, absoluteIndex }],
        };
      },
      { [SECTION_KEYS.selling]: [], [SECTION_KEYS.sourcing]: [] },
    );
  }, [formik.values, sourcingTypeId]);

  const buildSectionEntityOptions = (currentSalesUserIdKey, sectionEntries) => {
    const takenSalesUserIds = sectionEntries
      .map(({ row }) => {
        const userIdKey = getSalesPersonUserIdKey(row);
        return userIdKey === currentSalesUserIdKey
          ? null
          : formik.values[userIdKey];
      })
      .filter(id => id != null);

    return (
      entities
        ?.filter(entity => !takenSalesUserIds.includes(entity?.id))
        ?.map(entity => ({ label: entity?.name, value: entity?.id })) || []
    );
  };

  const buildRowConfig = ({ row, absoluteIndex }, sectionEntries) => {
    const userIdKey = getSalesPersonUserIdKey(row);
    const splitKey = getSalesPersonSplitKey(row);

    return {
      className: "grid grid-cols-1 items-start gap-4 sm:grid-cols-2",
      inputs: [
        {
          name: userIdKey,
          label: "Sales User",
          type: "multi-select",
          options: buildSectionEntityOptions(userIdKey, sectionEntries),
          className: "col-span-1",
        },
        {
          className:
            "grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2 sm:col-span-1",
          inputs: [
            {
              name: splitKey,
              label: "Split (0-1)",
              // Currency formatting rewrites partial edits (e.g. 0 -> 0.00000).
              type: "number",
              allowEmpty: true,
              step: 0.00001,
            },
            {
              name: "salesPeople",
              type: "removeDynamicInputs",
              deleteIndex: absoluteIndex,
              deleteKeys: Object.keys(row),
            },
          ],
        },
      ],
    };
  };

  const getNextRowIndex = () =>
    formik?.values?.salesPeople?.reduce((maxIndex, salePerson) => {
      const rowIndex = Object.keys(salePerson).reduce((maxKeyIndex, key) => {
        const keyIndex = Number(key.split("_")[1]) || 0;
        return Math.max(maxKeyIndex, keyIndex);
      }, 0);

      return Math.max(maxIndex, rowIndex);
    }, 0) + 1;

  const insertSalesPersonForSection = sectionTypeId => {
    if (sectionTypeId == null) return;

    const nextIndex = getNextRowIndex();
    const newRow = {
      [`salesUserId_${nextIndex}`]: null,
      [`split_${nextIndex}`]: 0,
      [`type_${nextIndex}`]: sectionTypeId,
      [`action_${nextIndex}`]: "removeDynamicInputs",
    };

    formik?.setFieldValue("salesPeople", [
      ...formik.values.salesPeople,
      newRow,
    ]);
    formik?.setFieldValue(`salesUserId_${nextIndex}`, null);
    formik?.setFieldValue(`split_${nextIndex}`, null);
    formik?.setFieldValue(`type_${nextIndex}`, sectionTypeId);
    formik?.setFieldValue(`action_${nextIndex}`, "removeDynamicInputs");
  };

  const handleSplitRatioEqually = () => {
    const splitRatios = getEqualSplitRatios(
      formik?.values?.salesPeople,
      formik?.values,
      invoiceSalesPersonTypes,
    );
    const updatedSplitValues = formik?.values?.salesPeople?.reduce(
      (updatedValues, salePerson, index) => {
        const splitKey = getSalesPersonSplitKey(salePerson);

        return splitKey
          ? { ...updatedValues, [splitKey]: splitRatios[index] }
          : updatedValues;
      },
      {},
    );

    formik?.setValues({
      ...formik.values,
      ...updatedSplitValues,
    });
  };

  const disabled = !formik?.isValid || loading;

  const balanceInputProps = {
    formik,
    className: "mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:gap-16",
    inputs: [
      {
        disabled: true,
        label: "Allocated",
        name: "allocated",
        type: "text",
      },
      {
        disabled: true,
        label: "Remaining",
        name: "remaining",
        type: "text-currency",
      },
    ],
  };

  const renderSection = ({ key, label }) => {
    const sectionEntries = sectionRowsByKey[key];
    const sectionTypeId =
      key === SECTION_KEYS.sourcing ? sourcingTypeId : sellingTypeId;
    const addDisabled = loading || sectionTypeId == null;
    const isCompact = sectionEntries.length > 0;
    const compactClasses = "py-1 text-xs";
    const fullSizeClasses = "py-4 text-sm";
    const enabledClasses =
      "cursor-pointer border-gray-300 text-gray-500 hover:border-gray-400 hover:bg-gray-50 hover:text-gray-700";
    const disabledClasses =
      "cursor-not-allowed border-gray-200 text-gray-300";

    return (
      <div key={key} className="mt-4">
        <h6 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-700">
          {label}
        </h6>
        {sectionEntries.length ? (
          <GridInputs
            formik={formik}
            className="grid gap-4 pb-4"
            inputs={sectionEntries.map(entry =>
              buildRowConfig(entry, sectionEntries),
            )}
          />
        ) : null}
        <button
          type="button"
          disabled={addDisabled}
          onClick={() => insertSalesPersonForSection(sectionTypeId)}
          className={`mt-2 w-full rounded border border-dashed text-center transition-colors ${
            isCompact ? compactClasses : fullSizeClasses
          } ${addDisabled ? disabledClasses : enabledClasses}`}
        >
          {isCompact ? `+ Add another ${label.toLowerCase()}` : `Add ${label}`}
        </button>
      </div>
    );
  };

  const hasAnyRows = formik?.values?.salesPeople?.length > 0;

  // Type resolution changes initialValues; don't allow edits that Formik
  // would discard when it reinitializes after the query completes.
  if (salesPersonTypesLoading) {
    return (
      <div className="flex flex-col gap-4">
        <p role="status">Loading sales person types...</p>
        <Button action="default" label="Cancel" onClick={closeModal} />
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="flex flex-1 flex-col text-2xl font-bold">
          <div>Edit Sales Person</div>
        </div>
        <div className="flex flex-wrap gap-4">
          <Button
            action="default"
            label="Cancel"
            onClick={() => {
              closeModal();
              reset();
              formik?.resetForm?.();
            }}
          />
          <Button
            label="Save"
            loading={loading}
            disabled={disabled}
            onClick={formik.submitForm}
          />
        </div>
      </div>
      <p>
        <span className="text-sm text-red-500">{error}</span>
      </p>
      <div className="max-h-[450px] overflow-y-auto sm:pr-4 lg:pr-10">
        {SECTIONS.map(renderSection)}
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-6 lg:gap-8">
        <div className="lg:col-span-5">
          <GridInputs {...balanceInputProps} />
        </div>
        <div className="mt-10 text-center">
          <Button
            label="Split Equally"
            disabled={!hasAnyRows}
            onClick={() => {
              handleSplitRatioEqually();
            }}
          />
        </div>
      </div>
    </div>
  );
};

const AddSalesPerson = props => {
  const { hasPermission } = useUser();
  const {
    invoice,
    additionalSalesPeople,
    trigger,
    label = "Edit Sales Person",
  } = props || {};

  const modalProps = {
    body: AddSalesPersonBody,
    closeOnBackdrop: false,
    hideCloseButton: true,
    scale: "md",
    invoice,
    additionalSalesPeople,
    disabled: !hasPermission("UPDATE_INVOICE_SALES_PERSON"),
  };

  const defaultTrigger = (
    <Button
      label={label}
      disabled={!hasPermission("UPDATE_INVOICE_SALES_PERSON")}
    />
  );

  return <Modal {...modalProps}>{trigger ?? defaultTrigger}</Modal>;
};

export default AddSalesPerson;
